<template>
  <div>
    <transition name="modal-fade">
      <div
        v-if="showModal"
        class="draggable-overlay"
        :class="{ 'dragging-active': !isInitialPosition }"
      >
        <div
          ref="modalRef"
          class="draggable-panel"
          :class="{ 'is-dragging': isDragging }"
          :style="modalStyle"
          @click.stop
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <div class="panel-header draggable">
            <div class="panel-header__text">
              <h6 class="panel-header__title">Last Interaction History</h6>
              <p class="panel-header__subtitle">View past outreach activity</p>
            </div>
            <div class="panel-header__actions">
              <div class="drag-handle" title="Drag to move">
                <Icon icon="mdi:drag" width="16" height="16" />
              </div>
              <button
                type="button"
                class="panel-header__close"
                @click="handleClose"
                aria-label="Close"
              >
                <Icon icon="mdi:close" width="18" height="18" />
              </button>
            </div>
          </div>

          <div class="channel-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="[
                'channel-tab',
                { active: tab.id === selectedChannel, dim: tabCount(tab.id) === 0 },
              ]"
              @click="handleTabClick(tab.id)"
            >
              <Icon :icon="tab.icon" width="14" height="14" />
              <span class="channel-tab__label">{{ tab.label }}</span>
              <span class="channel-tab__count">{{ tabCount(tab.id) }}</span>
            </button>
          </div>

          <div class="panel-body">
            <div class="modal-body-content">
              <div v-if="isLoading" class="loading-container">
                <LoadingBars size="sm" />
              </div>

              <div v-else-if="error" class="error-container">
                <Icon icon="mdi:alert-circle-outline" width="24" height="24" class="error-icon" />
                <p class="error-message">{{ errorMessage }}</p>
                <button type="button" class="btn btn-primary btn-sm" @click="() => refetch()">
                  Retry
                </button>
              </div>

              <div v-else-if="activityLogs && activityLogs.length > 0" class="interactions-list">
                <template
                  v-for="(group, groupIndex) in groupedActivityLogs"
                  :key="`group-${groupIndex}-${group.label}`"
                >
                  <div class="date-divider">{{ group.label }}</div>
                  <template
                    v-for="(activityLog, itemIndex) in group.items"
                    :key="activityLog.id || `${groupIndex}-${itemIndex}`"
                  >
                    <!-- Call interactions use CallAnalysisAccordion -->
                    <CallAnalysisAccordion
                      v-if="resolveTaskType(activityLog) === TaskType.call"
                      :call-sid="activityLog.callSid || null"
                      :activity-log="activityLog"
                      :is-expanded="expandedAccordionId === activityLog.id"
                      :recording-sid="activityLog.callLog?.recordingSid || null"
                      :recording-url="activityLog.callLog?.recordingUrl || null"
                      :call-status="activityLog.callLog?.status || null"
                      :logged-date="formattedDate(activityLog)"
                      :logged-time="formattedTime(activityLog)"
                      :performed-by="activityLog.createdBy?.fullName || null"
                      @expand="handleAccordionExpand(activityLog.id)"
                      @collapse="handleAccordionCollapse"
                      @switch-channel="handleSwitchChannel"
                    />

                    <!-- Non-call interactions -->
                    <div
                      v-else
                      class="interaction-card"
                      :class="{
                        'interaction-card--clickable': !!getTabForTaskType(
                          resolveTaskType(activityLog)
                        ),
                      }"
                      @click="handleCardClick(activityLog)"
                    >
                      <div
                        class="interaction-icon-badge"
                        :class="getIconBadgeClass(resolveTaskType(activityLog))"
                      >
                        <Icon
                          :icon="getInteractionIcon(resolveTaskType(activityLog))"
                          width="18"
                          height="18"
                          :class="getIconColorClass(resolveTaskType(activityLog))"
                        />
                      </div>

                      <div class="interaction-content">
                        <div class="interaction-header">
                          <div class="interaction-title-group">
                            <h6 class="interaction-title">
                              {{ getInteractionTitle(activityLog) }}
                            </h6>
                            <span
                              v-if="activityLog.createdBy?.fullName"
                              class="interaction-performer"
                            >
                              <Icon icon="mdi:account-outline" width="11" height="11" />
                              {{ activityLog.createdBy.fullName }}
                            </span>
                          </div>
                          <div class="interaction-meta">
                            <span class="interaction-date">{{ formattedDate(activityLog) }}</span>
                            <span class="interaction-time">{{ formattedTime(activityLog) }}</span>
                          </div>
                        </div>

                        <div
                          v-if="resolveTaskType(activityLog) !== TaskType.other"
                          class="social-card"
                          :class="getSocialCardClass(resolveTaskType(activityLog))"
                        >
                          <div class="social-card__header">
                            <Icon
                              :icon="getInteractionIcon(resolveTaskType(activityLog))"
                              width="14"
                              height="14"
                              class="social-card__icon"
                            />
                            <span class="social-card__label">{{
                              getSocialCardLabel(resolveTaskType(activityLog))
                            }}</span>
                            <span
                              v-if="activityLog.metadata?.hasAttachments"
                              class="social-card__attachment"
                            >
                              <Icon icon="mdi:attachment" width="12" height="12" />
                            </span>
                            <span
                              v-if="!!getTabForTaskType(resolveTaskType(activityLog))"
                              class="social-card__cta"
                            >
                              Open
                              <Icon icon="mdi:arrow-right" width="12" height="12" />
                            </span>
                          </div>
                          <p v-if="getPreviewContent(activityLog)" class="social-card__preview">
                            {{ getPreviewContent(activityLog) }}
                          </p>
                        </div>

                        <div
                          v-if="
                            resolveTaskType(activityLog) === TaskType.other &&
                            (activityLog.note || activityLog.description)
                          "
                          class="other-description"
                        >
                          <span>{{ activityLog.note || activityLog.description }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </div>

              <div v-else class="empty-container">
                <div class="empty-icon-wrapper">
                  <Icon icon="mdi:history" width="28" height="28" class="empty-icon" />
                </div>
                <div class="empty-text">
                  <p class="empty-title">
                    {{
                      selectedChannel === "all"
                        ? "No interactions yet"
                        : `No ${activeTabLabel} interactions yet`
                    }}
                  </p>
                  <p class="empty-description">
                    {{
                      selectedChannel === "all"
                        ? "Calls, emails, and messages with this contact will appear here."
                        : "Try a different channel or check back later."
                    }}
                  </p>
                </div>
              </div>

              <div
                v-if="
                  paginationData && Math.ceil(paginationData.total / paginationData.perPage) > 1
                "
                class="pagination-wrapper"
              >
                <TablePagination
                  :pagination="paginationData"
                  :per-page-options="[10, 25, 50]"
                  @page-change="handlePageChange"
                  @per-page-change="handlePerPageChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <EmailCompositionModal
      :show="showFollowUpComposer"
      :recipient-email="props.contactEmail"
      :accounts="emailAccounts"
      :initial-subject="followUpSubject"
      :initial-body="followUpBody"
      :contact-id="props.contactId"
      @close="showFollowUpComposer = false"
      @send="handleFollowUpSent"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useActivityLogs } from "@/composables/activityLogs/useActivityLogs";
import { TablePagination } from "@/components/ui";
import { TaskType } from "@/utils/enums";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import CallAnalysisAccordion from "@/components/call/CallAnalysisAccordion.vue";
import EmailCompositionModal from "@/components/bucket/email/EmailCompositionModal.vue";
import Unipile from "@/services/unipile/unipile";
import { getSocket } from "@/utils/socket";
import type { ActivityLogResponse } from "@/dtos/ActivityLog.dto";
import type { PaginationState } from "@/components/ui/table/types";

type ChannelTabId = "all" | TaskType;

interface Props {
  contactId: string;
  contactEmail?: string;
  initialChannel?: ChannelTabId;
}

const props = withDefaults(defineProps<Props>(), {
  contactEmail: "",
  initialChannel: "all",
});

const emit = defineEmits<{
  closeLastInteractionModal: [];
  navigateToTab: [tab: string, conversationId: string | null, messageId: string | null];
}>();

const instance = getCurrentInstance();
const $filters = instance?.appContext.config.globalProperties.$filters;
const store = useStore();

const showModal = ref(true);
const expandedAccordionId = ref<string | null>(null);
const showFollowUpComposer = ref(false);
const followUpSubject = ref("");
const followUpBody = ref("");
const emailAccounts = ref<any[]>([]);

const account = computed(() => store.getters.account.data);

const currentPage = ref(1);
const pageSize = ref(10);

const selectedChannel = ref<ChannelTabId>(props.initialChannel);

// Drag state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};
  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [".panel-body", ".channel-tabs", "button", "a", "input", "textarea"];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

const activityLogsParams = computed(() => {
  if (!props.contactId || !account.value?.organizationId) return null;
  return {
    contactId: props.contactId,
    organizationId: account.value.organizationId,
    page: currentPage.value,
    limit: pageSize.value,
    taskType: selectedChannel.value === "all" ? undefined : selectedChannel.value,
  };
});

const { data: activityLogsData, isLoading, error, refetch } = useActivityLogs(activityLogsParams);

const errorMessage = computed(() => {
  if (!error.value) return "";
  if (error.value instanceof Error) {
    return error.value.message;
  }
  return String(error.value);
});

const activityLogs = computed<ActivityLogResponse[]>(() => {
  if (!activityLogsData.value) return [];
  return activityLogsData.value.logs;
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const FULL_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const getActivityLogDate = (log: ActivityLogResponse): Date | null => {
  const value = log.loggedOn ?? log.movedOn ?? log.createdAt;
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateGroupLabel = (date: Date): string => {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff > 1 && dayDiff < 7) return WEEKDAY_FORMATTER.format(date);
  return FULL_DATE_FORMATTER.format(date);
};

interface ActivityLogGroup {
  label: string;
  items: ActivityLogResponse[];
}

const groupedActivityLogs = computed<ActivityLogGroup[]>(() => {
  const groups: ActivityLogGroup[] = [];
  let current: ActivityLogGroup | null = null;

  for (const log of activityLogs.value) {
    const date = getActivityLogDate(log);
    const label = date ? getDateGroupLabel(date) : "Earlier";
    if (!current || current.label !== label) {
      current = { label, items: [] };
      groups.push(current);
    }
    current.items.push(log);
  }

  return groups;
});

const paginationData = computed<PaginationState | null>(() => {
  if (!activityLogsData.value) return null;

  const paginated = activityLogsData.value;
  return {
    page: paginated.pagination.page,
    perPage: paginated.pagination.limit,
    total: paginated.pagination.total,
  };
});

const countsByType = computed(() => {
  return (
    activityLogsData.value?.countsByType ?? {
      all: 0,
      call: 0,
      email: 0,
      linkedIn: 0,
      whatsApp: 0,
      sms: 0,
      other: 0,
    }
  );
});

interface ChannelTab {
  id: ChannelTabId;
  label: string;
  icon: string;
}

const tabs: ChannelTab[] = [
  { id: "all", label: "All", icon: "mdi:view-list-outline" },
  { id: TaskType.call, label: "Calls", icon: "solar:phone-bold" },
  { id: TaskType.email, label: "Email", icon: "mdi:email-outline" },
  { id: TaskType.linkedIn, label: "LinkedIn", icon: "mdi:linkedin" },
  { id: TaskType.whatsApp, label: "WhatsApp", icon: "mdi:whatsapp" },
  { id: TaskType.sms, label: "SMS", icon: "mdi:message-text-outline" },
];

const tabCount = (id: ChannelTabId) => countsByType.value[id] ?? 0;

const handleTabClick = (id: ChannelTabId) => {
  if (selectedChannel.value === id) return;
  selectedChannel.value = id;
  currentPage.value = 1;
  expandedAccordionId.value = null;
};

const activeTabLabel = computed(
  () => tabs.find((t) => t.id === selectedChannel.value)?.label ?? "interaction"
);

const handlePageChange = (page: number) => {
  currentPage.value = page;
  const modalBody = document.querySelector(".modal-body-content");
  if (modalBody) {
    modalBody.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const handlePerPageChange = (perPage: number) => {
  pageSize.value = perPage;
  currentPage.value = 1;
  const modalBody = document.querySelector(".modal-body-content");
  if (modalBody) {
    modalBody.scrollTo({ top: 0, behavior: "smooth" });
  }
};

let socket: ReturnType<typeof getSocket> | null = null;

async function loadEmailAccounts() {
  try {
    const response = await Unipile.getConnectedChannels();
    const channels = response.data.data || [];
    emailAccounts.value = channels
      .filter((c: any) => c.type === "email")
      .map((account: any) => ({
        id: account.accountId,
        email: account.providerAccountId || account.name || "",
        name: account.providerAccountId || account.name || "",
        accountType: account.accountType,
        status: account.status,
        icon: account.icon,
      }));
  } catch (error) {
    console.error("Failed to load email accounts:", error);
  }
}

function handleSwitchChannel(payload: { channel: string; subject?: string; content?: string }) {
  if (payload.channel === "email") {
    followUpSubject.value = payload.subject || "";
    followUpBody.value = payload.content || "";
    showFollowUpComposer.value = true;
  }
}

function handleFollowUpSent() {
  showFollowUpComposer.value = false;
}

onMounted(() => {
  socket = getSocket();
  socket.on("call:analysis:complete", () => refetch());
  loadEmailAccounts();
});

onBeforeUnmount(() => {
  if (socket) {
    socket.off("call:analysis:complete");
  }
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});

const handleClose = () => {
  emit("closeLastInteractionModal");
};

const handleAccordionExpand = (id: string) => {
  expandedAccordionId.value = id;
};

const handleAccordionCollapse = () => {
  expandedAccordionId.value = null;
};

/** Maps a task type to the tab id used in TaskFollowupModal, or null for non-routable types. */
const getTabForTaskType = (taskType: string): string | null => {
  switch (taskType) {
    case TaskType.email:
      return "email";
    case TaskType.linkedIn:
      return "linkedin";
    case TaskType.whatsApp:
      return "whatsapp";
    case TaskType.sms:
      return "sms";
    default:
      return null;
  }
};

const resolveTaskType = (activityLog: ActivityLogResponse): string =>
  activityLog.taskType || TaskType.other;

/** The conversation/thread id to auto-open for this activity log, per channel. */
const getConversationIdForTaskType = (
  taskType: string,
  activityLog: ActivityLogResponse
): string | null => {
  switch (taskType) {
    case TaskType.email:
      return activityLog.metadata?.threadId || null;
    case TaskType.linkedIn:
    case TaskType.whatsApp:
      return activityLog.metadata?.conversationId || null;
    default:
      return null;
  }
};

/** The specific message to scroll to/highlight within the conversation, per channel. */
const getMessageIdForTaskType = (
  taskType: string,
  activityLog: ActivityLogResponse
): string | null => {
  switch (taskType) {
    case TaskType.linkedIn:
    case TaskType.whatsApp:
      return activityLog.metadata?.externalId || null;
    default:
      return null;
  }
};

const handleCardClick = (activityLog: ActivityLogResponse) => {
  const taskType = resolveTaskType(activityLog);
  const tab = getTabForTaskType(taskType);
  if (tab) {
    const conversationId = getConversationIdForTaskType(taskType, activityLog);
    const messageId = getMessageIdForTaskType(taskType, activityLog);
    emit("navigateToTab", tab, conversationId, messageId);
  }
  emit("closeLastInteractionModal");
};

const getPreviewContent = (activityLog: ActivityLogResponse): string | null => {
  const preview = activityLog.metadata?.preview || activityLog.metadata?.smsPreview;
  if (!preview) return null;
  return preview.length > 150 ? preview.substring(0, 150) + "..." : preview;
};

const getInteractionIcon = (taskType: string) => {
  switch (taskType) {
    case TaskType.call:
      return "mdi:phone-in-talk";
    case TaskType.email:
      return "mdi:email-outline";
    case TaskType.linkedIn:
      return "mdi:linkedin";
    case TaskType.whatsApp:
      return "mdi:whatsapp";
    case TaskType.sms:
      return "mdi:message-text-outline";
    case TaskType.other:
      return "mdi:dots-horizontal-circle-outline";
    default:
      return "mdi:dots-horizontal-circle-outline";
  }
};

const getIconBadgeClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.call:
      return "icon-badge--call";
    case TaskType.email:
      return "icon-badge--email";
    case TaskType.linkedIn:
      return "icon-badge--linkedin";
    case TaskType.whatsApp:
      return "icon-badge--whatsapp";
    case TaskType.sms:
      return "icon-badge--sms";
    case TaskType.other:
      return "icon-badge--other";
    default:
      return "icon-badge--other";
  }
};

const getInteractionTitle = (activityLog: ActivityLogResponse) => {
  const taskType = resolveTaskType(activityLog);
  const direction = activityLog.metadata?.direction;
  switch (taskType) {
    case TaskType.call:
      return direction === "inbound" ? "Incoming call" : "Call has been made";
    case TaskType.email:
      return direction === "inbound" ? "Email received from contact" : "Email sent to contact";
    case TaskType.linkedIn:
      return direction === "inbound" ? "LinkedIn message received" : "LinkedIn message sent";
    case TaskType.whatsApp:
      return direction === "inbound" ? "WhatsApp message received" : "WhatsApp message sent";
    case TaskType.sms:
      return direction === "inbound" ? "SMS received from contact" : "SMS sent to contact";
    case TaskType.other:
      return "Interaction logged";
    default:
      return "Interaction logged";
  }
};

const getSocialCardClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "social-card--email";
    case TaskType.linkedIn:
      return "social-card--linkedin";
    case TaskType.whatsApp:
      return "social-card--whatsapp";
    case TaskType.sms:
      return "social-card--sms";
    default:
      return "social-card--other";
  }
};

const getSocialCardLabel = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "EMAIL";
    case TaskType.linkedIn:
      return "LINKEDIN";
    case TaskType.whatsApp:
      return "WHATSAPP";
    case TaskType.sms:
      return "SMS";
    default:
      return "OTHER";
  }
};

const formattedDate = (activityLog: ActivityLogResponse) => {
  const dateValue = activityLog.loggedOn ?? activityLog.movedOn ?? activityLog.createdAt;
  if (!dateValue || !$filters) return "N/A";
  return $filters.getDateOnly(dateValue);
};

const formattedTime = (activityLog: ActivityLogResponse) => {
  const dateValue = activityLog.loggedOn ?? activityLog.movedOn ?? activityLog.createdAt;
  if (!dateValue || !$filters) return "N/A";
  return $filters.getTimeOnly(dateValue);
};

const getIconColorClass = (taskType: string) => {
  switch (taskType) {
    case TaskType.email:
      return "icon-color--email";
    case TaskType.call:
      return "icon-color--call";
    case TaskType.linkedIn:
      return "icon-color--linkedin";
    case TaskType.whatsApp:
      return "icon-color--whatsapp";
    case TaskType.sms:
      return "icon-color--sms";
    case TaskType.other:
      return "icon-color--other";
    default:
      return "icon-color--other";
  }
};
</script>

<style scoped>
.draggable-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10060;
  pointer-events: none;
}

.draggable-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.draggable-panel {
  background: #ffffff;
  border-radius: 12px;
  width: 560px;
  max-width: 90vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
  position: relative;
}

.draggable-panel.is-dragging {
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  cursor: grabbing;
  transition: none;
}

.panel-header {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  padding: 14px 16px;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.panel-header.draggable {
  cursor: grab;
  user-select: none;
}

.panel-header.draggable:active {
  cursor: grabbing;
}

.panel-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.panel-header__title {
  font-size: 1rem;
  color: white;
  margin: 0;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.panel-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.panel-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: rgba(255, 255, 255, 0.6);
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.15);
}

.panel-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.panel-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.channel-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #ececfb;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  flex-shrink: 0;
}

.channel-tabs::-webkit-scrollbar {
  height: 4px;
}

.channel-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.channel-tabs::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.channel-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: #f4f5ff;
  color: #5b5fc7;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
}

.channel-tab:hover:not(.active) {
  background: #e3e6ff;
}

.channel-tab.active {
  background: #2d3bff;
  color: #fff;
  box-shadow: 0 4px 14px rgba(45, 59, 255, 0.2);
}

.channel-tab.dim:not(.active) {
  opacity: 0.5;
}

.channel-tab__label {
  line-height: 1;
}

.channel-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  padding: 0 5px;
  height: 16px;
  border-radius: 999px;
  background: rgba(91, 95, 199, 0.15);
  color: #5b5fc7;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.channel-tab.active .channel-tab__count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.modal-body-content {
  padding: 16px 20px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  width: 100%;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  width: 100%;
  padding: 20px;
  gap: 16px;
}

.error-icon {
  color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
}

.btn {
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease-in-out;
}

.btn-primary {
  background-color: #3e3efe;
  border-color: #3e3efe;
  color: #ffffff;
}

.btn-primary:hover {
  background-color: #3230d6;
  border-color: #3230d6;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 0.7rem;
}

.interactions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.date-divider {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 2px;
  margin-top: 4px;
}

.date-divider:first-child {
  margin-top: 0;
}

.interaction-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s ease;
  cursor: default;
}

.interaction-card--clickable {
  cursor: pointer;
}

.interaction-card--clickable:hover {
  border-color: #c7d2fe;
  background: #fafafa;
  box-shadow: 0 2px 10px rgba(62, 62, 254, 0.07);
  transform: translateY(-1px);
}

.interaction-icon-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.icon-badge--email {
  background: #fef3c7;
  border-color: #fcd34d;
}

.icon-badge--linkedin {
  background: #dbeafe;
  border-color: #93c5fd;
}

.icon-badge--whatsapp {
  background: #dcfce7;
  border-color: #86efac;
}

.icon-badge--sms {
  background: #ede9fe;
  border-color: #c4b5fd;
}

.icon-badge--other {
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.icon-color--email :deep(.iconify),
.icon-color--email :deep(svg) {
  color: #d97706;
}

.icon-color--call :deep(.iconify),
.icon-color--call :deep(svg) {
  color: #3e3efe;
}

.icon-color--linkedin :deep(.iconify),
.icon-color--linkedin :deep(svg) {
  color: #0077b5;
}

.icon-color--whatsapp :deep(.iconify),
.icon-color--whatsapp :deep(svg) {
  color: #25d366;
}

.icon-color--sms :deep(.iconify),
.icon-color--sms :deep(svg) {
  color: #7c3aed;
}

.icon-color--other :deep(.iconify),
.icon-color--other :deep(svg) {
  color: #6b7280;
}

.interaction-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.interaction-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.interaction-title-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

.interaction-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.interaction-title::before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #3e3efe;
  flex-shrink: 0;
}

.interaction-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  padding-left: 12px;
}

.interaction-date {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.3;
  white-space: nowrap;
}

.interaction-time {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.3;
  white-space: nowrap;
}

.interaction-performer {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 500;
  color: #6366f1;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  border-radius: 20px;
  padding: 1px 7px 1px 4px;
  white-space: nowrap;
  margin-top: 3px;
  line-height: 1.6;
}

.social-card {
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.15s ease;
}

.social-card__header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.social-card__icon {
  flex-shrink: 0;
}

.social-card__label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.social-card__attachment {
  color: #9ca3af;
}

.social-card__cta {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.interaction-card--clickable:hover .social-card__cta {
  opacity: 1;
}

.social-card__preview {
  font-size: 12px;
  line-height: 1.4;
  margin: 4px 0 0;
  color: #4b5563;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.social-card--email {
  background: linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%);
  border-color: #fcd34d;
}

.social-card--email .social-card__icon,
.social-card--email .social-card__cta {
  color: #d97706;
}

.social-card--email .social-card__label {
  color: #b45309;
}

.social-card--linkedin {
  background: linear-gradient(135deg, #dbeafe 0%, #e0f2fe 100%);
  border-color: #7dd3fc;
}

.social-card--linkedin .social-card__icon,
.social-card--linkedin .social-card__cta {
  color: #0077b5;
}

.social-card--linkedin .social-card__label {
  color: #0369a1;
}

.social-card--whatsapp {
  background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%);
  border-color: #86efac;
}

.social-card--whatsapp .social-card__icon,
.social-card--whatsapp .social-card__cta {
  color: #16a34a;
}

.social-card--whatsapp .social-card__label {
  color: #15803d;
}

.social-card--sms {
  background: linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%);
  border-color: #c4b5fd;
}

.social-card--sms .social-card__icon,
.social-card--sms .social-card__cta {
  color: #7c3aed;
}

.social-card--sms .social-card__label {
  color: #6d28d9;
}

.social-card--other {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.social-card--other .social-card__icon {
  color: #6b7280;
}

.social-card--other .social-card__label {
  color: #4b5563;
}

.other-description {
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #4b5563;
  line-height: 1.5;
}

.interactions-list :deep(.call-analysis-accordion) {
  margin-top: 0;
}

.interactions-list :deep(.accordion-header-content) {
  padding: 10px 14px;
  gap: 10px;
}

.interactions-list :deep(.accordion-left-section) {
  gap: 10px;
}

.interactions-list :deep(.accordion-title) {
  font-size: 0.85rem;
}

.interactions-list :deep(.accordion-subtitle) {
  font-size: 0.75rem;
}

.interactions-list :deep(.accordion-status-badge) {
  padding: 2px 8px;
  font-size: 0.65rem;
}

.interactions-list :deep(.accordion-right-section) {
  gap: 10px;
}

.interactions-list :deep(.accordion-content) {
  padding: 14px;
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
  gap: 16px;
  padding: 40px 24px;
}

.empty-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  color: #6366f1;
}

.empty-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  max-width: 280px;
}

.empty-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.empty-description {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.pagination-wrapper {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
